import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { ApiError } from "@shared/api/client";
import { usePurgeAsset, useStaffAsset } from "@shared/api/hooks/useAssets";
import { formatHktDate, formatHktDateTime } from "@shared/lib/datetime";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// F-C — minimal asset detail + admin purge surface.
// No nav entry (admin reaches this by direct URL or audit drill-through).

function fmtBytes(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const AssetDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: asset, isLoading, error } = useStaffAsset(id);
  const purgeMut = usePurgeAsset();
  const [confirmInput, setConfirmInput] = useState("");
  const [reason, setReason] = useState("");

  const purged = asset?.state === "purged";
  const canPurge =
    confirmInput === "PURGE" && reason.trim().length >= 1 && !purged;

  const handlePurge = () => {
    if (!canPurge || !id) return;
    purgeMut.mutate(
      { assetId: id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success("Asset purged (bytes deleted; row kept as tombstone)");
          setConfirmInput("");
          setReason("");
        },
        onError: (err) =>
          toast.error(
            err instanceof ApiError && err.message
              ? err.message
              : "Purge failed",
          ),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-12" data-testid="loading-state">
        Loading...
      </div>
    );
  }
  if (error || !asset) {
    return (
      <div
        className="text-center py-12 text-destructive"
        data-testid="error-state"
      >
        Asset not found (or you lack permission to view it).
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="asset-detail-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">Asset details</h1>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <dl
          className="grid grid-cols-[12rem_1fr] gap-y-2 text-sm"
          data-testid="asset-fields"
        >
          <dt className="text-muted-foreground">Asset ID</dt>
          <dd className="font-mono">{asset.id}</dd>
          <dt className="text-muted-foreground">Kind</dt>
          <dd>{asset.kind}</dd>
          <dt className="text-muted-foreground">MIME</dt>
          <dd>{asset.mime_type}</dd>
          <dt className="text-muted-foreground">Size</dt>
          <dd>{fmtBytes(asset.size_bytes)}</dd>
          <dt className="text-muted-foreground">Owner</dt>
          <dd>
            {asset.owner_kind}/{asset.owner_id}
          </dd>
          <dt className="text-muted-foreground">State</dt>
          <dd data-testid="asset-state">{asset.state}</dd>
          <dt className="text-muted-foreground">Tier</dt>
          <dd>{asset.storage_tier}</dd>
          <dt className="text-muted-foreground">Uploaded by</dt>
          <dd>
            {asset.uploaded_by}
            {asset.uploaded_at
              ? ` (${formatHktDate(asset.uploaded_at)}, HKT)`
              : ""}
          </dd>
          {purged && (
            <>
              <dt className="text-muted-foreground">Purged at (HKT)</dt>
              <dd data-testid="purged-at">
                {asset.purged_at ? formatHktDateTime(asset.purged_at) : ""}
              </dd>
              <dt className="text-muted-foreground">Purge reason</dt>
              <dd data-testid="purge-reason">{asset.purge_reason}</dd>
            </>
          )}
        </dl>

        {!purged && (
          <fieldset
            className="border border-red-400 rounded p-4 space-y-3"
            data-testid="purge-fieldset"
          >
            <legend className="px-2 text-sm font-medium text-red-700">
              Danger zone — purge
            </legend>
            <p className="text-xs text-muted-foreground">
              Irreversible: bytes deleted from storage; row kept as a tombstone
              for audit. Content referencing this asset will render broken.
            </p>
            <div className="space-y-2">
              <Label>Type the word PURGE to confirm</Label>
              <Input
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                data-testid="purge-confirm-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason (recorded in audit log)</Label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border rounded px-3 py-2 min-h-[4rem]"
                maxLength={500}
                data-testid="purge-reason-input"
              />
            </div>
            <Button
              type="button"
              onClick={handlePurge}
              variant="destructive"
              size="sm"
              disabled={!canPurge || purgeMut.isPending}
              data-testid="purge-button"
            >
              Purge this asset
            </Button>
          </fieldset>
        )}
      </div>
    </div>
  );
};

export default AssetDetail;
